import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { FORM_MODE } from "../../config/constants";
import AppointmentFormFooter from "../Shared/AppointmentFormFooter";
import FormWrapper from "../Shared/FormWrapper";
import AlertRed from "../Shared/AlertRed";
import {
  APPOINTMENT_TYPES,
  APPOINTMENT_STATUS,
} from "../../config/appointment";
import { STATUS_COLOR_CLASSES } from "../../config/constants";
import ClientLookupNative from "../Shared/ClientLookupNative";
import CreateClientForm from "../Client/CreateClientForm";
import AddressLookupNative from "../Shared/AddressLookupNative";
import ServiceLookupNative from "../Shared/ServiceLookupNative";
import StatusDropdown from "../Shared/StatusDropdown";
import DateTimePicker from "../Shared/DateTimePicker/DateTimePicker";
import QuantityStepper from "../Shared/QuantityStepper";
import TextArea from "../Shared/TextArea";
import RadioGroup from "../Shared/RadioGroup";
import PriceInput from "../Shared/PriceInput";
import { getAvailabilities } from "../../api/availabilityApi";
import { AVAILABILITY_TYPES } from "../../config/availability";
import ErrorMessage from "../Shared/ErrorMessage";
import { getTimezoneName } from "../../utility/timezoneUtils";
import { mapApiError } from "../../utils/formErrorMapper";
import useScrollToFirstError from "../../hooks/useScrollToFirstError";

const AppointmentForm = ({
  defaultAppointment,
  defaultClient,
  defaultClientAddress,
  saveAppointment,
  patchAppointment,
  closeDrawerHandler,
  mode,
  onCancelHandler,
  setIsFormDirty,
  selectedDate,
  onModalTitleChange,
}) => {
  const maxCharacters = 150;
  const [selectedServices, setSelectedServices] = useState(() => {
    if (defaultAppointment) {
      return defaultAppointment.services;
    }
    return [];
  });
  const [noteCharCount, setNoteCharCount] = useState(() => {
    if (defaultAppointment?.note) {
      return defaultAppointment.note.length;
    }
    return 0;
  });
  const [cancellationCharCount, setCancellationCharCount] = useState(() => {
    if (defaultAppointment?.cancellationReason) {
      return defaultAppointment.cancellationReason.length;
    }
    return 0;
  });
  const [defaultMobileAddress, setDefaulMobileAddress] =
    useState(defaultClientAddress);
  const [selectedClientId, setSelectedClientId] = useState(defaultClient?._id || null);
  const [step, setStep] = useState("appointment"); // "appointment" | "create-client"
  const [createClientDefaultName, setCreateClientDefaultName] = useState("");
  const [clientDisplayName, setClientDisplayName] = useState(defaultClient?.fullName || null);
  const [banner, setBanner] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmit, setLastSubmit] = useState(null); // { data, shouldNotifyClient }

  // Helper function to get default start time
  const getDefaultStartTime = () => {
    if (defaultAppointment?.startTime) {
      return defaultAppointment.startTime;
    }

    if (mode === FORM_MODE.CREATE && selectedDate) {
      // Set to selected date at next available hour
      const defaultTime = new Date(selectedDate);
      const currentHour = new Date().getHours();
      const nextHour = currentHour < 23 ? currentHour + 1 : 9; // Default to 9 AM if it's late
      defaultTime.setHours(nextHour, 0, 0, 0);
      return defaultTime.toISOString();
    }

    return new Date().toISOString();
  };
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty, dirtyFields },
    watch,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      client: defaultAppointment?.client ?? defaultClient,
      type: defaultAppointment?.type
        ? defaultAppointment?.type
        : APPOINTMENT_TYPES.MOBILE,
      serviceAddress:
        defaultAppointment?.serviceAddress ?? defaultClientAddress,
      services: defaultAppointment?.services ? defaultAppointment.services : [],
      travelFee: defaultAppointment?.travelFee
        ? parseFloat(defaultAppointment?.travelFee)
        : 0,
      startTime: getDefaultStartTime(),
      status: defaultAppointment?.status
        ? APPOINTMENT_STATUS.find(
            (status) => status.id === defaultAppointment.status
          )?.id
        : APPOINTMENT_STATUS.find((status) => status.id === "confirmed")?.id,
      note: defaultAppointment?.note ? defaultAppointment.note : "",
      cancellationReason: defaultAppointment?.cancellationReason
        ? defaultAppointment.cancellationReason
        : "",
      clientTimezone:
        defaultAppointment?.clientTimezone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
  const appointmentType = watch("type");
  const currentStatus = APPOINTMENT_STATUS.find(
    (status) => status.id === watch("status")
  );
  const currentAppointmentType = watch("type");
  const watchedServiceAddress = watch("serviceAddress");

  // Resolve the timezone the backend will actually store this appointment at.
  // Backend preserves wall-clock time at the LOCATION's timezone, so we show that
  // — never the browser timezone (which would be actively misleading cross-TZ).
  const resolvedTimeZoneId =
    mode === FORM_MODE.UPDATE
      ? defaultAppointment?.timeZoneId
      : watchedServiceAddress?.timeZoneId;
  let timezoneMessage;
  let timezoneMessageMuted = false;
  if (resolvedTimeZoneId) {
    timezoneMessage = `Times are in ${
      getTimezoneName(resolvedTimeZoneId) || resolvedTimeZoneId
    }`;
  } else if (mode === FORM_MODE.CREATE && watchedServiceAddress?.placeId) {
    // Mobile address: timezone is resolved server-side at save via Google Maps.
    // Echo the address so the user sees we know *where* this is, even without an IANA name yet.
    const rawAddress = watchedServiceAddress?.address || "";
    const parts = rawAddress.split(",").map((p) => p.trim()).filter(Boolean);
    const shortAddressLabel =
      parts.length >= 2 ? `${parts[0]}, ${parts[1]}` : rawAddress;
    timezoneMessage = shortAddressLabel
      ? `Times will be saved in the local timezone of ${shortAddressLabel}.`
      : "Times will be saved in the service address's local timezone.";
  } else {
    timezoneMessage =
      "Pick an address first — we'll save this time in that location's timezone.";
    timezoneMessageMuted = true;
  }

  const onSelectService = (item) => {
    if (!item) return;
    const newService = {
      serviceId: item.id,
      name: item.name,
      price: item.price,
      duration: item.duration,
      quantity: 1,
    };

    setSelectedServices((prev) => {
      const updated = [...prev, newService];
      setValue("services", updated, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors("services");
      return updated;
    });
  };

  const handleIncrease = (index) => {
    const updated = [...selectedServices];
    updated[index].quantity += 1;
    setSelectedServices(updated);
    setValue("services", updated, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors("services");
  };

  const handleDecrease = (index) => {
    const updated = [...selectedServices];
    if (updated[index].quantity > 1) {
      updated[index].quantity -= 1;
      setSelectedServices(updated);
    }
    setValue("services", updated, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors("services");
  };

  const handleRemove = (index) => {
    setSelectedServices((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      setValue("services", updated, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors("services");
      return updated;
    });
  };

  const { data: availabilities } = useQuery({
    queryKey: ["availabilities"],
    queryFn: getAvailabilities,
  });

  const hasMobile = availabilities?.some(
    (a) =>
      a.availabilityType === AVAILABILITY_TYPES.REGION ||
      a.availabilityType === AVAILABILITY_TYPES.DISTANCE
  ) ?? false;
  const hasFixed = availabilities?.some(
    (a) => a.availabilityType === AVAILABILITY_TYPES.FIXED
  ) ?? false;

  const appointmentTypeOptions = [
    {
      id: APPOINTMENT_TYPES.MOBILE,
      title: "Mobile",
      description: "I'm travelling to the client.",
    },
    {
      id: APPOINTMENT_TYPES.FIXED,
      title: "Fixed",
      description: "The client comes to me.",
    },
  ];

  const fixedLocations = availabilities
    ? availabilities
        .filter((a) => a.availabilityType === AVAILABILITY_TYPES.FIXED)
        .map((a) => ({
          id: a._id,
          availabilityId: a._id,
          placeId: a.baseAddress.placeId,
          name: a.baseAddress.address,
          timeZoneId: a.timeZoneId,
        }))
    : [];

  const isDataValid = (data) => {
    let isValid = true;

    if (!data.client?._id) {
      setError("client", {
        type: "manual",
        message: "Client is required",
      });
      isValid = false;
    }
    if (!data.serviceAddress?.placeId) {
      setError("serviceAddress", {
        type: "manual",
        message: "Appointment address is required",
      });
      isValid = false;
    }
    if (!data.services?.length) {
      setError("services", {
        type: "manual",
        message: "At least one service is required",
      });
      isValid = false;
    }
    if (!data.startTime) {
      setError("startTime", {
        type: "manual",
        message: "Start time is required",
      });
      isValid = false;
    }
    return isValid;
  };

  const onSubmit = async (data, shouldNotifyClient = false) => {
    if (isSubmitting) return;
    const isValid = isDataValid(data);
    if (!isValid) return;

    setBanner(null);
    setLastSubmit({ data, shouldNotifyClient });
    setIsSubmitting(true);
    try {
      if (mode === FORM_MODE.CREATE) {
        await saveAppointment({
          data,
          shouldNotifyClient,
        });
      } else if (mode === FORM_MODE.UPDATE) {
        const updatedData = {};
        const { id } = defaultAppointment;
        if (dirtyFields.client) updatedData.client = data.client;
        if (dirtyFields.type) updatedData.type = data.type;
        if (dirtyFields.serviceAddress)
          updatedData.serviceAddress = data.serviceAddress;
        if (dirtyFields.services) updatedData.services = data.services;
        if (dirtyFields.travelFee) updatedData.travelFee = data.travelFee;
        if (dirtyFields.startTime) {
          updatedData.startTime = data.startTime;
          updatedData.clientTimezone = data.clientTimezone;
        }
        if (dirtyFields.status) updatedData.status = data.status;
        if (dirtyFields.note) updatedData.note = data.note;
        if (dirtyFields.cancellationReason)
          updatedData.cancellationReason = data.cancellationReason;
        await patchAppointment({
          id,
          data: { ...updatedData },
          shouldNotifyClient,
        });
      }
    } catch (err) {
      const result = mapApiError(err, { setError });
      if (result.handled) return;
      if (result.kind === "auth") return;
      setBanner(result.banner);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = () => {
    handleSubmit((formData) => onSubmit(formData, false))();
  };

  const handleSaveAndEmail = () => {
    handleSubmit((formData) => onSubmit(formData, true))();
  };

  const handleRetry = () => {
    if (lastSubmit) onSubmit(lastSubmit.data, lastSubmit.shouldNotifyClient);
  };

  const onSelectAddress = (serviceAddress) => {
    if (!serviceAddress) {
      setValue("serviceAddress", null, { shouldDirty: true });
      return;
    }

    // For fixed locations, use availabilityId. For mobile, use placeId (Google Places ID)
    const addressData = {
      address: serviceAddress?.name,
      full: serviceAddress?.name,
    };

    if (serviceAddress?.availabilityId) {
      // Fixed location - consistent with mobile
      addressData.placeId = serviceAddress.placeId; // Google Places ID
      addressData.availabilityId = serviceAddress.availabilityId; // MongoDB ObjectId for reference
      if (serviceAddress.timeZoneId) {
        addressData.timeZoneId = serviceAddress.timeZoneId;
      }
    } else {
      // Mobile location - use Google Places ID (timezone resolved server-side at save)
      addressData.placeId = serviceAddress?.id;
    }

    setValue("serviceAddress", addressData, { shouldDirty: true });
    clearErrors("serviceAddress");
  };

  const onSelectClient = (client) => {
    setValue("client", { _id: client?.id }, { shouldDirty: true });
    clearErrors("client");

    if (mode === FORM_MODE.CREATE) {
      if (client?.serviceAddress?.full && client?.serviceAddress?.placeId) {
        const addressData = {
          full: client.serviceAddress.full,
          placeId: client.serviceAddress.placeId,
        };
        setValue("serviceAddress", addressData, { shouldDirty: true });
        setDefaulMobileAddress(addressData);
        clearErrors("serviceAddress");
      } else if (!client) {
        setValue("serviceAddress", null, { shouldDirty: true });
        setDefaulMobileAddress(null);
      }
    }

    setClientDisplayName(client?.name || null);
    setSelectedClientId(client?.id || null);
  };

  const onCreateNewClient = (searchQuery) => {
    setCreateClientDefaultName(searchQuery || "");
    setStep("create-client");
  };

  const onClientCreated = (newClient) => {
    setValue("client", { _id: newClient._id }, { shouldDirty: true });
    clearErrors("client");

    if (newClient.serviceAddress?.full && newClient.serviceAddress?.placeId) {
      const addressData = {
        full: newClient.serviceAddress.full,
        placeId: newClient.serviceAddress.placeId,
      };
      setValue("serviceAddress", addressData, { shouldDirty: true });
      setDefaulMobileAddress(addressData);
      clearErrors("serviceAddress");
    }

    setClientDisplayName(newClient.fullName || newClient.firstName);
    setSelectedClientId(newClient._id);
    setStep("appointment");
  };

  // clear out default appointments set for mobile appointments
  useEffect(() => {
    if (
      mode === FORM_MODE.CREATE &&
      appointmentType === APPOINTMENT_TYPES.FIXED
    ) {
      setValue("serviceAddress", null);
      setDefaulMobileAddress(null);
    }
  }, [appointmentType, mode, setValue]);

  // Auto-select appointment type when only one availability type exists
  useEffect(() => {
    if (mode === FORM_MODE.UPDATE) return;
    if (!availabilities) return;
    if (hasFixed && !hasMobile) {
      setValue("type", APPOINTMENT_TYPES.FIXED);
    } else if (hasMobile && !hasFixed) {
      setValue("type", APPOINTMENT_TYPES.MOBILE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilities, hasMobile, hasFixed, mode, setValue]);

  // Auto-select fixed location when there's only one
  useEffect(() => {
    if (mode === FORM_MODE.UPDATE) return;
    if (currentAppointmentType !== APPOINTMENT_TYPES.FIXED) return;
    if (fixedLocations.length === 1) {
      onSelectAddress(fixedLocations[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAppointmentType, fixedLocations.length, mode]);

  useEffect(() => {
    setIsFormDirty(isDirty);
  }, [isDirty, setIsFormDirty]);

  useScrollToFirstError(errors);

  useEffect(() => {
    if (step === "create-client") {
      onModalTitleChange?.("Add New Client");
    } else {
      onModalTitleChange?.(
        mode === FORM_MODE.CREATE ? "Create Appointment" : "Edit Appointment"
      );
    }
  }, [step, mode, onModalTitleChange]);

  if (step === "create-client") {
    return (
      <CreateClientForm
        defaultName={createClientDefaultName}
        onClientCreated={onClientCreated}
        onCancel={() => {
          setIsFormDirty(isDirty);
          setStep("appointment");
        }}
        onDirtyChange={(dirty) => setIsFormDirty(dirty)}
        cancelLabel="Back to Appointment"
      />
    );
  }

  return (
    <FormWrapper onSubmit={handleSubmit((formData) => onSubmit(formData, false))}>
        {banner ? (
          <div className="mb-4">
            <AlertRed
              title={banner.title}
              message={banner.message}
              onRetry={handleRetry}
            />
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">

          <div className="col-span-full">
            <ClientLookupNative
              key={`Client-${selectedClientId || "none"}`}
              label="Client"
              placeholder={"Search for a client..."}
              defaultValue={{ name: clientDisplayName || defaultClient?.fullName }}
              onSelect={onSelectClient}
              isError={errors.client}
              disabled={mode === FORM_MODE.UPDATE}
              onCreateNewClient={mode === FORM_MODE.CREATE ? onCreateNewClient : null}
            />
            {errors.client && <ErrorMessage message={errors.client.message} />}
          </div>
          <div className="col-span-full">
            <RadioGroup
              label="Appointment type"
              description="Are you travelling to the client or is it at a fixed location?"
              name="type"
              options={appointmentTypeOptions}
              register={register}
              required="This field is required"
            />
          </div>
          <div className="col-span-full">
            {currentAppointmentType === APPOINTMENT_TYPES.MOBILE ? (
              <AddressLookupNative
                key={`MobileAddress-${selectedClientId || 'none'}`}
                label={"Appointment address"}
                placeholder={"Search for an address..."}
                onSelect={onSelectAddress}
                defaultValue={{
                  name:
                    defaultAppointment?.serviceAddress?.full ??
                    defaultMobileAddress?.full,
                }}
                isError={errors.serviceAddress}
              />
            ) : (
              <AddressLookupNative
                key={"FixedAddress"}
                label={"Appointment address"}
                placeholder={"Search for an address..."}
                onSelect={onSelectAddress}
                fixedLocationMode={true}
                showSearchOption={false}
                defaultOptions={fixedLocations}
                defaultValue={
                  mode === FORM_MODE.CREATE && fixedLocations.length === 1
                    ? { name: fixedLocations[0].name, placeId: fixedLocations[0].placeId }
                    : { name: defaultAppointment?.serviceAddress?.full, placeId: defaultAppointment?.serviceAddress?.placeId }
                }
                isError={errors.serviceAddress}
              />
            )}
            {errors.serviceAddress && (
              <ErrorMessage message={errors.serviceAddress.message} />
            )}
          </div>
          <div className="col-span-full">
            <ServiceLookupNative
              key={"services"}
              label={"Services"}
              onSelect={onSelectService}
              selectedServices={selectedServices.map(
                (service) => service.serviceId
              )}
            />
            {selectedServices.map((service, index) => {
              return (
                <QuantityStepper
                  key={service.serviceId}
                  serviceName={service.name}
                  quantity={service.quantity}
                  pricePerUnit={service.price}
                  handleIncrease={() => handleIncrease(index)}
                  handleDecrease={() => handleDecrease(index)}
                  onRemove={() => handleRemove(index)}
                />
              );
            })}
            {errors.services && (
              <ErrorMessage message={errors.services.message} />
            )}
          </div>
          {currentAppointmentType === APPOINTMENT_TYPES.MOBILE && (
            <div className="col-span-full">
              <PriceInput
                label="Travel fee"
                optional
                id="travelFee"
                name="travelFee"
                type="number"
                step="0.01"
                min="0"
                errormessage={errors?.price?.message}
                {...register("travelFee", {
                  valueAsNumber: true,
                  validate: (value) =>
                    value >= 0 || "Price cannot be less than 0",
                })}
              />
            </div>
          )}
          <div className="col-span-full">
            <DateTimePicker
              label={"Date & Time"}
              onChange={(dateWithTimezone) => {
                setValue("startTime", dateWithTimezone.date?.toISOString(), {
                  shouldDirty: true,
                });
                setValue("clientTimezone", dateWithTimezone.timezone, {
                  shouldDirty: true,
                });
              }}
              defaultSelected={
                defaultAppointment?.startTime
                  ? new Date(defaultAppointment.startTime)
                  : mode === FORM_MODE.CREATE && selectedDate
                  ? new Date(getDefaultStartTime())
                  : new Date()
              }
              timeZoneId={defaultAppointment?.timeZoneId}
            />
            <p
              className={`text-sm mt-1 ${
                timezoneMessageMuted
                  ? "text-ink-400 italic"
                  : "text-ink-500"
              }`}
            >
              {timezoneMessage}
            </p>
            {errors.startTime && (
              <ErrorMessage message={errors.startTime.message} />
            )}
          </div>
          <div className="col-span-full">
            <StatusDropdown
              key={"Status"}
              label={
                <p className="flex items-center gap-2">
                  <span
                    className={`${
                      STATUS_COLOR_CLASSES[currentStatus?.colour]
                    } forced-colors:bg-[Highlight] inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-transparent`}
                  />
                  Status
                </p>
              }
              data={APPOINTMENT_STATUS}
              defaultSelected={currentStatus}
              onSelect={(status) => {
                if (!status) return;
                setValue("status", status?.id, {
                  shouldDirty: true,
                });
              }}
            />
          </div>
          {currentStatus?.id === "cancelled" && (
            <div className="col-span-full">
              <TextArea
                label="Reason for cancellation"
                optional
                id="cancellationReason"
                name="cancellationReason"
                placeholder="Let the client know why (e.g., illness, weather)"
                errormessage={errors?.cancellationReason?.message}
                charCount={cancellationCharCount}
                maxLength={maxCharacters}
                {...register("cancellationReason", {
                  maxLength: {
                    value: maxCharacters,
                    message: `Cancellation Reason cannot exceed ${maxCharacters} characters`,
                  },
                  onBlur: (e) => {
                    setValue("cancellationReason", e.target.value.trim(), {
                      shouldValidate: true,
                    });
                  },
                  onChange: (e) => {
                    setCancellationCharCount(e.target.value.trim().length);
                    setValue("cancellationReason", e.target.value, {
                      shouldValidate: true,
                    });
                  },
                })}
              />
            </div>
          )}
          <div className="col-span-full">
            <TextArea
              label="Notes"
              optional
              id="Note"
              name="Note"
              placeholder="Anything the team should know (allergies, access codes, preferences)"
              errormessage={errors?.note?.message}
              charCount={noteCharCount}
              maxLength={maxCharacters}
              {...register("note", {
                maxLength: {
                  value: maxCharacters,
                  message: `Notes cannot exceed ${maxCharacters} characters`,
                },
                onBlur: (e) => {
                  setValue("note", e.target.value.trim(), {
                    shouldValidate: true,
                  });
                },
                onChange: (e) => {
                  setNoteCharCount(e.target.value.trim().length);
                  setValue("note", e.target.value, {
                    shouldValidate: true,
                  });
                },
              })}
            />
          </div>
        </div>
        <AppointmentFormFooter
          secondaryCaption="Cancel"
          secondaryClickHandler={onCancelHandler}
          onSave={handleSave}
          onSaveAndEmail={handleSaveAndEmail}
          isLoading={isSubmitting}
          requiresVerification={false}
          mode={mode}
        />
    </FormWrapper>
  );
};

export default AppointmentForm;
